// analysisSections.js
//
// Pure state transitions for the analysis flow: the Current Formulations bucket
// and the Formulation Analysis sections rows move between.
//
// Extracted from ScenarioAnalysis.vue so the state machine is testable without
// mounting Vue. Every function here takes plain objects and either returns new
// data or mutates the object it was handed — no reactivity, no network, no DOM.
//
// THE MODEL
//   currentFormulations[]  source bucket; rows not yet under analysis
//   analysisSections[]     each = one proposed target + the rows switching to it
//
// A row lives in exactly one place. Moving it is a splice out and a push in.

import { formulationsMatch } from './formulationString';

const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

/**
 * Roman-numeral label for a section by position, falling back to the ordinal.
 * @param {number} index
 * @returns {string}
 */
export function sectionLabel(index) {
  return ROMAN_NUMERALS[index] || String(index + 1);
}

/**
 * A fresh, empty analysis section.
 *
 * @param {number} id
 * @returns {object}
 */
export function createSection(id) {
  return {
    id,
    targetFormulation: '',
    // Which RTU category the target was picked from: 'commercial' | '503b' | null.
    // Recorded from the picker section clicked, because a concentration offered as
    // both products is indistinguishable from its string alone.
    targetRtuCategory: null,
    targetCost: null,
    collapsed: false,
    // Second (split) target. Selection-only today: not wired into metrics or the
    // impact summary.
    splitMode: 'single', // 'single' | 'split'
    targetFormulation2: '',
    targetCost2: null,
    rows: [],
  };
}

/**
 * Shape a formulation-data row into a Current Formulations bucket row.
 *
 * @param {object} source - a row from the analysis store's formulationData
 * @param {number} id
 * @returns {object}
 */
export function toCurrentRow(source, id) {
  return {
    id,
    formulation: source.formulation,
    infusions: source.infusions ?? null,
    // No server price source; user-entered.
    costPerInfusion: source.costPerInfusion ?? null,
    currentWaste: source.currentWaste ?? source.wastePercentage ?? null,
    currentBagsPerDay: source.currentBagsPerDay ?? null,
    currentMinRate: source.currentMinRate ?? null,
    currentMaxRate: source.currentMaxRate ?? null,
  };
}

/**
 * Convert a bucket row into a section row, adding the empty updated_* fields the
 * impact calculation fills in.
 *
 * @param {object} row
 * @returns {object}
 */
export function toSectionRow(row) {
  return {
    ...row,
    updatedWaste: null,
    updatedBagsPerDay: null,
    updatedMinRate: null,
    updatedMaxRate: null,
    updatedInfusions: null,
  };
}

/**
 * Strip a section row back to bucket shape, dropping the proposal metrics.
 *
 * @param {object} row
 * @returns {object}
 */
export function toBucketRow(row) {
  return {
    id: row.id,
    formulation: row.formulation,
    infusions: row.infusions,
    costPerInfusion: row.costPerInfusion ?? null,
    currentWaste: row.currentWaste ?? null,
    currentBagsPerDay: row.currentBagsPerDay ?? null,
    currentMinRate: row.currentMinRate ?? null,
    currentMaxRate: row.currentMaxRate ?? null,
  };
}

/**
 * Is this row the section's own target carrying through unchanged?
 *
 * Locked rows aren't changes: they can't be dragged out, their cost is read-only,
 * and they're excluded from the impact calculation.
 *
 * @param {object} section
 * @param {object} row
 * @returns {boolean}
 */
export function isRowLocked(section, row) {
  if (formulationsMatch(row.formulation, section.targetFormulation)) return true;
  return (
    section.splitMode === 'split' &&
    !!section.targetFormulation2 &&
    formulationsMatch(row.formulation, section.targetFormulation2)
  );
}

/**
 * Move a row out of the bucket and into a section. Mutates both.
 *
 * @returns {object|null} the moved row, or null when not found
 */
export function moveRowToSection(currentFormulations, section, rowId) {
  const index = currentFormulations.findIndex((r) => r.id === rowId);
  if (index === -1) return null;
  const [row] = currentFormulations.splice(index, 1);
  const sectionRow = toSectionRow(row);
  section.rows.push(sectionRow);
  return sectionRow;
}

/**
 * Move a row from one section to another. Mutates both.
 *
 * @returns {object|null}
 */
export function moveRowBetweenSections(fromSection, toSection, rowId) {
  const index = fromSection.rows.findIndex((r) => r.id === rowId);
  if (index === -1) return null;
  const [row] = fromSection.rows.splice(index, 1);
  toSection.rows.push(row);
  return row;
}

/**
 * Return a row from a section to the bucket. Mutates both.
 *
 * @returns {object|null}
 */
export function returnRowToBucket(currentFormulations, section, rowId) {
  const index = section.rows.findIndex((r) => r.id === rowId);
  if (index === -1) return null;
  const [row] = section.rows.splice(index, 1);
  const bucketRow = toBucketRow(row);
  currentFormulations.push(bucketRow);
  return bucketRow;
}

/**
 * When a target is picked, pull the matching bucket row in automatically so the
 * user doesn't have to drag in the formulation they just proposed.
 *
 * @param {Array} currentFormulations
 * @param {object} section
 * @param {string} target - the newly selected formulation
 */
export function autoMatchTarget(currentFormulations, section, target) {
  if (!target) return;
  // Already present in this section? Nothing to do.
  if (section.rows.some((r) => formulationsMatch(r.formulation, target))) return;
  const index = currentFormulations.findIndex((r) => formulationsMatch(r.formulation, target));
  if (index === -1) return;
  moveRowToSection(currentFormulations, section, currentFormulations[index].id);
}

/**
 * The current -> proposed pairs that represent REAL changes, for the impact
 * calculation and the saved payload. Locked carry-through rows are excluded.
 *
 * @param {Array} analysisSections
 * @returns {Array<{section: object, row: object}>}
 */
export function changeRows(analysisSections) {
  const rows = [];
  analysisSections.forEach((section) => {
    if (!section.targetFormulation) return;
    section.rows.forEach((row) => {
      if (isRowLocked(section, row)) return;
      rows.push({ section, row });
    });
  });
  return rows;
}

/**
 * Every section row that has a target, changed or not — the cumulative view's
 * input (it shows the whole resulting formulary, not only the deltas).
 *
 * @param {Array} analysisSections
 * @returns {Array<{section: object, row: object}>}
 */
export function allSectionMappings(analysisSections) {
  const mappings = [];
  analysisSections.forEach((section) => {
    if (!section.targetFormulation) return;
    section.rows.forEach((row) => mappings.push({ section, row }));
  });
  return mappings;
}

/**
 * Proposed formulations grouped by target, summing infusions. Several current
 * formulations converging on one proposal collapse into a single row.
 *
 * @param {Array} analysisSections
 * @returns {Array<{formulation: string, infusions: number, rtuCategory: string|null}>}
 */
export function groupProposedFormulations(analysisSections) {
  const groups = new Map();
  allSectionMappings(analysisSections).forEach(({ section, row }) => {
    const key = section.targetFormulation;
    const infusions = row.updatedInfusions != null ? row.updatedInfusions : row.infusions || 0;
    if (groups.has(key)) {
      groups.get(key).infusions += infusions;
    } else {
      groups.set(key, {
        formulation: key,
        infusions,
        rtuCategory: section.targetRtuCategory || null,
      });
    }
  });
  return Array.from(groups.values());
}

/**
 * The full Current-side mapping: every row under analysis, plus every untouched
 * bucket row carrying through unchanged.
 *
 * @returns {Array<{id: number, formulation: string, infusions: number|null, unchanged: boolean}>}
 */
export function buildCurrentMapping(analysisSections, currentFormulations) {
  const rows = allSectionMappings(analysisSections).map(({ section, row }) => ({
    id: row.id,
    formulation: row.formulation,
    infusions: row.infusions,
    // A section's own target carries through unchanged.
    unchanged: isRowLocked(section, row),
  }));
  currentFormulations.forEach((row) => {
    rows.push({
      id: row.id,
      formulation: row.formulation,
      infusions: row.infusions,
      unchanged: true,
    });
  });
  return rows;
}

/**
 * The full Proposed-side mapping: grouped targets merged with untouched bucket
 * rows (which propose to themselves).
 *
 * @returns {Array<{formulation: string, infusions: number, unchanged: boolean, rtuCategory: string|null}>}
 */
export function buildProposedMapping(analysisSections, currentFormulations) {
  const groups = new Map();
  groupProposedFormulations(analysisSections).forEach((p) => {
    groups.set(p.formulation, { ...p, unchanged: false });
  });
  currentFormulations.forEach((row) => {
    const key = row.formulation;
    const infusions = row.infusions || 0;
    if (groups.has(key)) {
      // A proposed target coincides with an untouched formulation — combine.
      groups.get(key).infusions += infusions;
    } else {
      groups.set(key, { formulation: key, infusions, unchanged: true, rtuCategory: null });
    }
  });
  return Array.from(groups.values());
}
